import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { rtlog } from '@/components/custom/route-transition/core/debug'

// The OUTER pager's second member — light, flat, no stack: swiping One ↔ Two exercises the outer lateral
// travel over a pane that contains a whole nav stack on one side and plain content on the other.
export const Route = createFileRoute('/_app/dev-resources/transitions/two')({
  component: Pane,
})

/**
 * THE INTERACTIVITY PROBE for `interactiveIncoming` (the playground is the only surface that opts in).
 *
 * Tap it while the pager is still moving — flick One → Two and hit it during the settle, or catch a settling
 * pager and tap instead of dragging — and it logs WHICH LAYER received the tap and WHAT PHASE the surface was in
 * when it did. Both are read off the DOM the engine already stamps, so the probe adds no state of its own:
 * `data-rt-layer="preview"` means the tap reached the shadow-router pane of a route that has not committed yet,
 * which is the whole capability; `data-rt-state` is the controller's phase (see `incomingInteractive`).
 *
 * The reading lands in the engine's rolling buffer, so the HUD at the bottom of the playground shows it, and
 * `window.__rtDump()` still has it afterwards. A `settling`/`finishing` preview tap is the prototype working; a
 * tap that logs nothing at all was swallowed — either by `inert` (the gate said no) or by the drag's one-shot
 * click swallower (the press travelled, so it was a swipe and not a tap).
 */
function InteractionProbe() {
  return (
    <Button
      type="button"
      variant="outline"
      className="self-start"
      onClick={(event) => {
        const el = event.currentTarget
        rtlog('probe-tap', {
          layer: el.closest('[data-rt-layer]')?.getAttribute('data-rt-layer') ?? 'live',
          phase: el.closest<HTMLElement>('[data-rt-surface]')?.dataset.rtState ?? 'idle',
        })
      }}
    >
      Tap me mid-transition
    </Button>
  )
}

function Pane() {
  return (
    <div className="flex flex-col gap-3 p-4">
      <h2 className="text-lg font-semibold">Two</h2>
      <InteractionProbe />
      <p className="text-muted-foreground text-sm">
        A flat pane beside the stacked one. Swipe back toward One and watch the HUD: the pane must never move against
        the finger.
      </p>
      <div className="flex flex-col gap-2">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="rounded-md border p-3 text-sm">
            Two row {i + 1}
          </div>
        ))}
      </div>
    </div>
  )
}
